'use client';

import { useEffect } from 'react';

const KIT_URL = 'https://use.typekit.net/xrx3zmi.css';

/**
 * Loads the Adobe Fonts (Typekit) stylesheet without blocking render.
 *
 * The link is server-rendered with media="print" so the browser's preload
 * scanner starts the download immediately, but the stylesheet doesn't sit on
 * the critical path. Once it has loaded, media is flipped to "all" and the
 * fonts apply. Cost of this is a short FOUT on cold loads.
 */
const TypekitLoader = (): React.ReactElement => {
    useEffect(() => {
        const link =
            document.querySelector<HTMLLinkElement>('link[data-typekit]');
        if (!link || link.media === 'all') return;

        // The sheet may already have loaded before hydration, in which case the
        // load event has been and gone. link.sheet is populated once the CSSOM
        // object exists, even while media doesn't match, so it's a reliable
        // "already done" check.
        let raf = 0;
        const swap = () => {
            raf = requestAnimationFrame(() => {
                link.media = 'all';
            });
        };

        if (link.sheet) swap();
        else link.addEventListener('load', swap, { once: true });

        return () => {
            cancelAnimationFrame(raf);
            link.removeEventListener('load', swap);
        };
    }, []);

    return (
        <>
            <link rel="preconnect" href="https://use.typekit.net" />
            <link
                rel="preconnect"
                href="https://p.typekit.net"
                crossOrigin="anonymous"
            />
            <link rel="stylesheet" href={KIT_URL} media="print" data-typekit />
            <noscript>
                <link rel="stylesheet" href={KIT_URL} />
            </noscript>
        </>
    );
};

export default TypekitLoader;
